
import React, { useState, useEffect, useRef } from 'react';
import { UserProfile, Community, SiteConfig, Meeting, LandingData } from '../types';
import { supabase } from '../services/supabaseClient';

interface AdminPanelProps {
  users: UserProfile[];
  communities: Community[];
  meetings: Meeting[];
  config: SiteConfig | null;
  onUpdateConfig: (newConfig: Partial<SiteConfig>) => void;
  onLogout: () => void;
  onViewUser: (user: UserProfile) => void;
  onRefresh: () => void;
  addToast: (message: string, type?: 'success' | 'error') => void;
}

export const AdminPanel: React.FC<AdminPanelProps> = ({ users, communities, meetings, config, onLogout, onViewUser, onRefresh, addToast }) => {
  const [tab, setTab] = useState<'dashboard' | 'users' | 'clubs' | 'landing' | 'site'>('dashboard');
  const [clubSubTab, setClubSubTab] = useState<'all' | 'pending'>('all');
  const [landingData, setLandingData] = useState<LandingData | null>(null);
  const [siteConfig, setSiteConfig] = useState<SiteConfig>(config || {
    logo_url: '', favicon_url: '', og_image_url: '', primary_color: '#da251d', font_family: 'Inter',
    seo_title: 'Random Coffee Vietnam', seo_keywords: '', seo_description: '', rules_text: '', privacy_text: ''
  });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadType, setUploadType] = useState<'logo' | 'favicon' | 'og'>('logo');

  useEffect(() => {
    supabase.from('landing_content').select('*').eq('id', 'main').single().then(({ data }) => data && setLandingData(data.data));
  }, []);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const { data, error } = await supabase.storage.from('site-assets').upload(`${uploadType}-${Date.now()}`, file);
    if (!error) {
      const { data: { publicUrl } } = supabase.storage.from('site-assets').getPublicUrl(data.path);
      if (uploadType === 'logo') setSiteConfig({...siteConfig, logo_url: publicUrl});
      if (uploadType === 'favicon') setSiteConfig({...siteConfig, favicon_url: publicUrl});
      if (uploadType === 'og') setSiteConfig({...siteConfig, og_image_url: publicUrl});
    }
  };

  const saveLanding = async () => {
    const { error } = await supabase.from('landing_content').upsert({ id: 'main', data: landingData });
    addToast(error ? 'Ошибка сохранения' : 'Лендинг обновлен');
  };

  const saveSite = async () => {
    const { error } = await supabase.from('site_config').upsert({ id: 'main', ...siteConfig });
    addToast(error ? 'Ошибка сохранения' : 'Настройки сайта сохранены');
  };

  const inputClasses = "w-full bg-gray-50 p-4 rounded-2xl font-bold border border-gray-100 outline-none focus:ring-2 focus:ring-black";
  const labelClasses = "text-[10px] font-black uppercase text-gray-400 block mb-2 mt-4 ml-1";

  return (
    <div className="bg-white rounded-[3.5rem] shadow-2xl border border-gray-100 flex min-h-[85vh] overflow-hidden">
      <aside className="w-72 bg-gray-50 border-r border-gray-100 p-10 flex flex-col gap-2">
        <button onClick={() => setTab('dashboard')} className={`p-4 rounded-2xl text-[10px] font-black uppercase text-left transition-all ${tab === 'dashboard' ? 'bg-black text-white shadow-xl' : 'text-gray-400'}`}>📊 Дашборд</button>
        <button onClick={() => setTab('users')} className={`p-4 rounded-2xl text-[10px] font-black uppercase text-left transition-all ${tab === 'users' ? 'bg-black text-white shadow-xl' : 'text-gray-400'}`}>👥 Участники</button>
        <button onClick={() => setTab('clubs')} className={`p-4 rounded-2xl text-[10px] font-black uppercase text-left transition-all ${tab === 'clubs' ? 'bg-black text-white shadow-xl' : 'text-gray-400'}`}>🏘️ Клубы</button>
        <button onClick={() => setTab('landing')} className={`p-4 rounded-2xl text-[10px] font-black uppercase text-left transition-all ${tab === 'landing' ? 'bg-black text-white shadow-xl' : 'text-gray-400'}`}>🏠 Лендинг</button>
        <button onClick={() => setTab('site')} className={`p-4 rounded-2xl text-[10px] font-black uppercase text-left transition-all ${tab === 'site' ? 'bg-black text-white shadow-xl' : 'text-gray-400'}`}>⚙️ Сайт</button>
        <button onClick={onLogout} className="p-4 rounded-2xl text-[10px] font-black uppercase text-red-600 bg-red-50 mt-auto">Выход</button>
      </aside>

      <main className="flex-1 p-16 overflow-y-auto">
        <input type="file" ref={fileInputRef} className="hidden" onChange={handleFileUpload} />
        
        {tab === 'dashboard' && (
          <div className="space-y-12 animate-fadeIn">
            <h2 className="text-4xl font-black uppercase tracking-tighter">Статистика</h2>
            <div className="grid grid-cols-3 gap-8">
              <div className="p-10 bg-gray-50 rounded-[2.5rem] border border-gray-100 cursor-pointer" onClick={() => setTab('users')}>
                <p className="text-[10px] font-black text-gray-400 uppercase mb-2">Всего людей</p>
                <p className="text-5xl font-black">{users.length}</p>
              </div>
              <div className="p-10 bg-gray-50 rounded-[2.5rem] border border-gray-100 cursor-pointer" onClick={() => setTab('clubs')}>
                <p className="text-[10px] font-black text-gray-400 uppercase mb-2">Клубов</p>
                <p className="text-5xl font-black">{communities.length}</p>
              </div>
              <div className="p-10 bg-gray-50 rounded-[2.5rem] border border-gray-100">
                <p className="text-[10px] font-black text-gray-400 uppercase mb-2">Заявок на кофе</p>
                <p className="text-5xl font-black">{meetings.length}</p>
              </div>
            </div>
          </div>
        )}

        {tab === 'users' && (
          <div className="space-y-6 animate-fadeIn">
            <h2 className="text-3xl font-black uppercase">Участники ({users.length})</h2>
            <div className="grid grid-cols-1 gap-4">
              {users.map(u => (
                <div key={u.id} onClick={() => onViewUser(u)} className="p-6 bg-gray-50 rounded-3xl flex items-center justify-between border border-gray-100 cursor-pointer hover:bg-white hover:shadow-lg transition-all">
                  <div className="flex items-center gap-4">
                    <img src={u.photo} className="w-12 h-12 rounded-xl object-cover" />
                    <div><p className="font-black text-sm uppercase">{u.name}</p><p className="text-[10px] text-gray-400 font-bold uppercase">{u.activitySphere} • {u.city}</p></div>
                  </div>
                  <button className="px-4 py-2 bg-black text-white rounded-xl text-[9px] font-black uppercase">Анкета</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === 'clubs' && (
          <div className="space-y-8 animate-fadeIn">
            <div className="flex gap-4">
              <button onClick={() => setClubSubTab('all')} className={`px-6 py-3 rounded-xl text-[10px] font-black uppercase ${clubSubTab === 'all' ? 'bg-black text-white' : 'bg-gray-100'}`}>Все клубы</button>
              <button onClick={() => setClubSubTab('pending')} className={`px-6 py-3 rounded-xl text-[10px] font-black uppercase ${clubSubTab === 'pending' ? 'bg-black text-white' : 'bg-gray-100'}`}>На модерации</button>
            </div>
            <div className="grid grid-cols-1 gap-4">
              {communities.filter(c => clubSubTab === 'all' ? true : c.status === 'pending').map(c => (
                <div key={c.id} className="p-6 bg-gray-50 rounded-3xl flex items-center justify-between border border-gray-100">
                  <div className="flex items-center gap-4">
                    <img src={c.logo} className="w-12 h-12 rounded-xl object-cover" />
                    <div><p className="font-black text-sm uppercase">{c.name}</p><p className="text-[10px] text-gray-400 font-bold uppercase">{c.status} • {c.city}</p></div>
                  </div>
                  <div className="flex gap-2">
                    {c.status === 'pending' && <button onClick={() => addToast('Клуб одобрен')} className="px-6 py-2 bg-green-500 text-white rounded-xl text-[10px] font-black uppercase">Одобрить</button>}
                    <button className="px-6 py-2 bg-gray-100 text-[10px] font-black uppercase rounded-xl">Детали</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === 'landing' && landingData && (
          <div className="space-y-12 animate-fadeIn max-w-4xl">
            <h2 className="text-4xl font-black uppercase tracking-tighter">Лендинг</h2>
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div><label className={labelClasses}>Текст кнопки (Join)</label><input className={inputClasses} value={landingData.hero_button} onChange={e => setLandingData({...landingData, hero_button: e.target.value})} /></div>
                <div><label className={labelClasses}>Заголовок "Как это работает"</label><input className={inputClasses} value={landingData.steps_title || ''} onChange={e => setLandingData({...landingData, steps_title: e.target.value})} /></div>
              </div>
              <div><label className={labelClasses}>Hero Заголовок</label><input className={inputClasses} value={landingData.hero_title} onChange={e => setLandingData({...landingData, hero_title: e.target.value})} /></div>
              <div><label className={labelClasses}>Footer Текст</label><input className={inputClasses} value={landingData.footer_text} onChange={e => setLandingData({...landingData, footer_text: e.target.value})} /></div>
              
              <h4 className="text-[10px] font-black uppercase text-red-600 mt-12 mb-4 border-b pb-2">Шаги инструкции (4 шт)</h4>
              <div className="grid grid-cols-2 gap-6">
                {landingData.steps.map((s, i) => (
                  <div key={i} className="p-6 bg-gray-50 rounded-3xl border border-gray-100 space-y-4">
                    <input className="w-full bg-white p-3 rounded-xl font-black text-xs uppercase" value={s.title} onChange={e => {
                      const ns = [...landingData.steps]; ns[i].title = e.target.value; setLandingData({...landingData, steps: ns});
                    }} />
                    <textarea className="w-full bg-white p-3 rounded-xl text-xs h-20" value={s.desc} onChange={e => {
                      const ns = [...landingData.steps]; ns[i].desc = e.target.value; setLandingData({...landingData, steps: ns});
                    }} />
                  </div>
                ))}
              </div>
            </div>
            <button onClick={saveLanding} className="w-full py-6 bg-black text-white font-black rounded-3xl uppercase tracking-widest shadow-2xl hover:bg-red-600 transition-all">Обновить контент</button>
          </div>
        )}

        {tab === 'site' && (
          <div className="space-y-12 animate-fadeIn max-w-4xl">
            <h2 className="text-4xl font-black uppercase tracking-tighter">Настройки сайта</h2>
            
            <section className="grid grid-cols-3 gap-6">
               <div onClick={() => { setUploadType('logo'); fileInputRef.current?.click(); }} className="aspect-square bg-gray-50 rounded-3xl border-2 border-dashed border-gray-200 flex flex-col items-center justify-center cursor-pointer overflow-hidden">
                 {siteConfig.logo_url ? <img src={siteConfig.logo_url} className="w-full h-full object-cover" /> : <p className="text-[10px] font-black uppercase text-gray-400">Логотип</p>}
               </div>
               <div onClick={() => { setUploadType('favicon'); fileInputRef.current?.click(); }} className="aspect-square bg-gray-50 rounded-3xl border-2 border-dashed border-gray-200 flex flex-col items-center justify-center cursor-pointer overflow-hidden">
                 {siteConfig.favicon_url ? <img src={siteConfig.favicon_url} className="w-full h-full object-cover" /> : <p className="text-[10px] font-black uppercase text-gray-400">Фавикон</p>}
               </div>
               <div onClick={() => { setUploadType('og'); fileInputRef.current?.click(); }} className="aspect-square bg-gray-50 rounded-3xl border-2 border-dashed border-gray-200 flex flex-col items-center justify-center cursor-pointer overflow-hidden">
                 {siteConfig.og_image_url ? <img src={siteConfig.og_image_url} className="w-full h-full object-cover" /> : <p className="text-[10px] font-black uppercase text-gray-400">OG Сниппет</p>}
               </div>
            </section>

            <div className="grid grid-cols-2 gap-8">
              <section className="space-y-4">
                <h4 className="text-[11px] font-black uppercase border-b pb-2">SEO</h4>
                <div><label className={labelClasses}>Title</label><input className={inputClasses} value={siteConfig.seo_title} onChange={e => setSiteConfig({...siteConfig, seo_title: e.target.value})} /></div>
                <div><label className={labelClasses}>Keywords</label><input className={inputClasses} value={siteConfig.seo_keywords} onChange={e => setSiteConfig({...siteConfig, seo_keywords: e.target.value})} /></div>
                <div><label className={labelClasses}>Description</label><textarea className={`${inputClasses} h-32`} value={siteConfig.seo_description} onChange={e => setSiteConfig({...siteConfig, seo_description: e.target.value})} /></div>
              </section>
              <section className="space-y-4">
                <h4 className="text-[11px] font-black uppercase border-b pb-2">Юридическая инфа</h4>
                <div><label className={labelClasses}>Правила клуба</label><textarea className={`${inputClasses} h-40`} value={siteConfig.rules_text} onChange={e => setSiteConfig({...siteConfig, rules_text: e.target.value})} /></div>
                <div><label className={labelClasses}>Политика конфиденциальности</label><textarea className={`${inputClasses} h-40`} value={siteConfig.privacy_text} onChange={e => setSiteConfig({...siteConfig, privacy_text: e.target.value})} /></div>
                <div><label className={labelClasses}>Цвет (#XXXXXX)</label><input className={inputClasses} value={siteConfig.primary_color} onChange={e => setSiteConfig({...siteConfig, primary_color: e.target.value})} /></div>
              </section>
            </div>
            <button onClick={saveSite} className="w-full py-6 bg-black text-white font-black rounded-3xl uppercase tracking-widest shadow-2xl hover:bg-red-600 transition-all">Сохранить всё</button>
          </div>
        )}
      </main>
    </div>
  );
};
